import { FileSourceEnum } from '@systemeio/file-manager'
import {
  BreadcrumbsData,
  DateRangePicker,
  FieldLabel,
  FormInputNumber,
  Hint,
  PrimaryButton,
  Tooltip,
  getDate,
} from '@systemeio/ui'
import { useGlobalAvailability } from 'modules/calendar/availability/hooks/use-global-availabilities'
import { staticMinutes } from 'modules/calendar/constants'
import {
  EventDateRangeModeEnum,
  EventTypeEnum,
} from 'modules/calendar/events/enums/event-type-enum'
import { getDateAfter30Days, getDateOnly } from 'modules/calendar/utils/date-time-formatters'
import { SettingsSection } from 'modules/profile/components/settings-section'
import { useCalendars } from 'modules/profile/integrations/google/hooks/use-calendars'
import React, { useEffect, useState } from 'react'
import { SubmitHandler, useForm, UseFormSetValue } from 'react-hook-form'
import { getUserDomainAccount } from 'shared/api/user-api'
import BreadcrumbsDashboard from 'shared/components/breadcrumbs'
import TimeDelayPicker from 'shared/components/date-picker/time-delay-picker'
import {
  convertSecondsToTimeValue,
  convertTimeValueToSeconds,
} from 'shared/components/date-picker/time-delay-picker/utils'
import { AvatarUploader } from 'shared/components/file-uploader/avatar-uploader'
import { ImageUploaderWithPreviewImage } from 'shared/components/file-uploader/image-uploader-with-preview-image'
import FormInputRhf from 'shared/components/form-input-rhf'
import FormSelectRhf from 'shared/components/form-select-rhf'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import RadioGroupRhf from 'shared/components/radio-group-rhf'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { filterPassedTime } from 'shared/utils/date-time-format'
import { getToday } from 'shared/utils/get-dates'
import { EventLocationTypeEnum, EventTimeZoneTypeEnum, defaultValues } from '../constants'
import { useCheckZoomIntegration } from '../hooks/use-check-zoom-integration'
import {
  EventCreateFormInterface,
  EventEditFormInterface,
  EventFormErrors,
  EventFormFields,
  EventViewInterface,
  FormCustomFields,
} from '../types/event-interface'
import { AddToGoogleCalendar } from './add-to-google-calendar'
import { EventAvailability } from './event-availability-fields'
import { LocationTypeFields } from './location-type-fields'
import { advancedTextEditorExtensions, TipTapEditor } from '@systemeio/tip-tap'
import { HostEmailAddress } from './host-email-address'
import { TimeType } from 'shared/components/date-picker/time-delay-picker/constants'
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component'
import { twJoin } from 'tailwind-merge'

interface EventFormProps {
  onSubmit: SubmitHandler<EventCreateFormInterface | EventEditFormInterface>
  eventData?: EventViewInterface
  errors: EventFormErrors | null
  breadcrumbsData: BreadcrumbsData[]
  eventType: EventTypeEnum
  limitReached?: boolean
  isFetchingEventLimit?: boolean
}

type EventFormType = (EventCreateFormInterface | EventEditFormInterface) & FormCustomFields

export const CUSTOM_FIELD_VALUE_ID = -1

export function EventForm({
  onSubmit,
  eventData,
  errors: submitErrors,
  breadcrumbsData,
  eventType,
  isFetchingEventLimit,
  limitReached,
}: EventFormProps) {
  const { t, tICU } = useLocoTranslation()
  const { user, intlLocale, avatarUrl } = useUser()

  const [isFetching, setIsFetching] = useState(false)
  const [isChanged, setIsChanged] = useState<boolean>(false)

  const { isZoomIntegrated, isLoading: isZoomIntegratedLoading } = useCheckZoomIntegration()

  const { hasGoogleCalendars, isLoading: isGoogleCalendarsLoading } = useCalendars()

  const [selectedTime, setSelectedTime] = useState({
    nearestBookRange: convertSecondsToTimeValue(
      eventData?.nearestBookRange != null ? eventData.nearestBookRange : 7200,
    ),
    cancellationDeadline: {
      ...convertSecondsToTimeValue(
        eventData?.cancellationDeadline != null ? eventData.cancellationDeadline : 0,
      ),
      timeType:
        eventData?.cancellationDeadline != null
          ? convertSecondsToTimeValue(eventData?.cancellationDeadline).timeType
          : TimeType.HOURS,
    },
  })

  const {
    control,
    watch,
    setValue,
    formState: { errors },
    handleSubmit,
    reset,
    clearErrors,
    getValues,
    setError,
    register,
  } = useForm<EventFormType>({
    defaultValues: {
      ...defaultValues,
      eventType: eventType,
      ownerId: user?.id,
      hostAvatarUrl: `${avatarUrl || ''}`,
      startDate: getDateOnly(getToday().toISOString()),
      endDate: getDateAfter30Days(),
    },
  })

  const handleManualSetValue = ((name, value, options) => {
    setIsChanged(true)

    return setValue(name, value, options)
  }) as UseFormSetValue<EventFormType>

  const { availability: globalAvailability } = useGlobalAvailability(!eventData)

  const isGlobalAvailabilityCreated = Boolean(
    eventData?.globalUserAvailability.dailySettings?.length ||
      globalAvailability?.dailySettings?.length,
  )

  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setValue(
      'useGlobalAvailability',
      eventData ? eventData.useGlobalAvailability : !!isGlobalAvailabilityCreated,
    )
  }, [isGlobalAvailabilityCreated])

  useEffect(() => {
    if (eventData && !mounted) {
      reset({
        ...eventData,
        hostAvatarUrl: eventData.hostAvatarUrl || avatarUrl || '',
      })
      if (eventData.eventDuration && !staticMinutes.includes(eventData.eventDuration)) {
        setValue('customEventDuration', eventData.eventDuration)
        setValue('eventDuration', CUSTOM_FIELD_VALUE_ID)
      }
      if (eventData.startTimeIncrement && !staticMinutes.includes(eventData.startTimeIncrement)) {
        setValue('customStartTimeIncrement', eventData.startTimeIncrement)
        setValue('startTimeIncrement', CUSTOM_FIELD_VALUE_ID)
      }
      if (!isZoomIntegrated && !isZoomIntegratedLoading) {
        setValue(
          'locations',
          eventData.locations.filter(x => {
            return x.locationType !== EventLocationTypeEnum.Zoom
          }),
        )
      }
      if (!hasGoogleCalendars && !isGoogleCalendarsLoading) {
        setValue(
          'locations',
          eventData.locations.filter(x => {
            return x.locationType !== EventLocationTypeEnum.GoogleMeet
          }),
        )
      }
      setMounted(true)
    }
  }, [
    eventData,
    mounted,
    isZoomIntegrated,
    isZoomIntegratedLoading,
    reset,
    avatarUrl,
    hasGoogleCalendars,
    setValue,
    isGoogleCalendarsLoading,
  ])

  useEffect(() => {
    if (!eventData) {
      getUserDomainAccount().then(res => {
        if (!eventData) {
          setValue('hostName', res.data.displayName)
          setValue('hostAvatarUrl', res.data.profileImage)
        }
      })
    }
  }, [])

  useEffect(() => {
    if (submitErrors) {
      Object.entries(submitErrors).forEach(([field, message]) => {
        if (field === 'dailySettings') {
          setError('localUserAvailability.dailySettings', { message })
          return
        }
        setError(field as keyof EventFormFields, { message })
      })
    }
  }, [submitErrors])

  useEffect(() => {
    register('nearestBookRange')
    register('cancellationDeadline')
  }, [register])

  const onFormSubmit = async (
    values: (EventCreateFormInterface | EventEditFormInterface) & FormCustomFields,
  ) => {
    try {
      setIsFetching(true)
      if (!values.startDate) {
        setError('startDate', { message: t('dashboard.validation.empty_error.title') })
        return
      }

      if (values.eventDuration === CUSTOM_FIELD_VALUE_ID) {
        values.eventDuration = values.customEventDuration!
        delete values.customEventDuration
      }
      if (values.startTimeIncrement === CUSTOM_FIELD_VALUE_ID) {
        values.startTimeIncrement = values.customStartTimeIncrement!
        delete values.customStartTimeIncrement
      }
      clearErrors()
      await onSubmit(values)
      setIsChanged(false)
    } catch (error) {
      setIsChanged(true)
    } finally {
      setIsFetching(false)
    }
  }
  useEffect(() => {
    // clear errors on field change
    const subscription = watch((value, { name }) => {
      if (name) {
        clearErrors(name)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, clearErrors])

  const minAvailibleDate = getDate(watch('startDate'), user?.timezone)
  const maxAvailibleDate = (() => {
    switch (watch('dateRangeMode')) {
      case EventDateRangeModeEnum.Indefinite: {
        return undefined
      }
      case EventDateRangeModeEnum.FarOutBookRange: {
        const days = watch('farOutBookRange')

        if (typeof days !== 'number') {
          return undefined
        }

        const futureDate = new Date()
        futureDate.setDate(futureDate.getDate() + days)

        const futureDateString = futureDate.toISOString()

        return getDate(futureDateString, user?.timezone)
      }
      case EventDateRangeModeEnum.Range: {
        return getDate(watch('endDate'), user?.timezone)
      }
    }
  })()

  return (
    <>
      <form
        onSubmit={e => {
          document.activeElement instanceof HTMLElement && document.activeElement.blur()
          handleSubmit(onFormSubmit)(e)
        }}
        className="flex flex-col gap-5"
      >
        <div className={'flex items-center justify-between'}>
          <BreadcrumbsDashboard data={breadcrumbsData} isFetching={isFetching} />
          <Tooltip
            mode={'hover'}
            label={t('dashboard.calendar.event_limit_tooltip')}
            disabled={!limitReached}
          >
            <PrimaryButton
              isFetching={isFetching || isFetchingEventLimit}
              type="submit"
              className="self-center"
              disabled={limitReached}
            >
              {t('dashboard.calendar.save_and_preview')}
            </PrimaryButton>
          </Tooltip>
        </div>

        <div className="bg-background-secondary flex flex-col gap-5 rounded-md p-5">
          <SettingsSection headingTitle={t('dashboard.calendar.event_details')}>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <FormInputRhf
                control={control}
                name="name"
                setValue={handleManualSetValue}
                label={<FieldLabel label={t('global.name')} />}
                error={errors.name?.message}
                required
              />
              <div className="[&>div]:flex-1 flex gap-4">
                <FormSelectRhf
                  control={control}
                  name="eventDuration"
                  data={[
                    ...staticMinutes.map(min => ({
                      id: min,
                      caption: tICU('global.name_of_times.minutes_with_count_icu', {
                        count: min,
                      }),
                    })),
                    { id: CUSTOM_FIELD_VALUE_ID, caption: t('global.custom') },
                  ]}
                  label={<FieldLabel label={t('dashboard.calendar.event_duration')} />}
                  setValue={handleManualSetValue}
                  rules={{ required: t('dashboard.validation.empty_error.title') }}
                  required
                  error={errors.eventDuration?.message}
                  withoutCloseIcon
                />
                {watch('eventDuration') === CUSTOM_FIELD_VALUE_ID && (
                  <FormInputRhf
                    control={control}
                    name="customEventDuration"
                    setValue={handleManualSetValue}
                    rules={{ required: t('dashboard.validation.empty_error.title') }}
                    error={errors.customEventDuration?.message}
                    label={<FieldLabel label={t('global.name_of_times.minutes.plural')} />}
                    required
                    type="number"
                    className="flex-1"
                  />
                )}
              </div>
              <FormInputRhf
                control={control}
                name="hostName"
                setValue={handleManualSetValue}
                label={<FieldLabel label={t('dashboard.calendar.event_host_name')} />}
                placeholder={t('dashboard.calendar.event_host_name')}
                error={errors.hostName?.message}
                required
              />
              <div className="flex flex-col gap-1">
                <div className={'[&>label]:w-fit flex flex-col'}>
                  <FieldLabel label={t('dashboard.calendar.host_photo')} />
                  <AvatarUploader
                    source={FileSourceEnum.CalendarHostAvatar}
                    onSetPhoto={dataFile => {
                      handleManualSetValue('hostAvatarUrl', dataFile.path)
                    }}
                  >
                    <img
                      className={'border-brand-primary rounded-full border-2 object-cover '}
                      alt={watch('hostName')}
                      src={watch('hostAvatarUrl')}
                      width={60}
                      height={60}
                    />
                  </AvatarUploader>
                </div>
                {errors.hostAvatarUrl?.message && (
                  <div>
                    <FieldErrorAndDescription error={errors.hostAvatarUrl.message} />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <LocationTypeFields
                  locations={watch('locations')}
                  setValue={handleManualSetValue}
                  error={errors.locations?.message}
                  eventType={eventType}
                />
                <ImageUploaderWithPreviewImage
                  className={'order-1 sm:order-2'}
                  source={FileSourceEnum.Membership}
                  label={
                    <Hint label={t('dashboard.calendar.custom_logo_hint')}>
                      {t('dashboard.calendar.custom_logo')}
                    </Hint>
                  }
                  error={errors.customLogo?.message}
                  value={watch('customLogo') ? { path: watch('customLogo') as string } : null}
                  previewClassName="h-[60px] w-[60px]"
                  onRemove={() => {
                    handleManualSetValue('customLogo', null)
                  }}
                  isRounded={true}
                  aspectRatio={1}
                  onSelect={dataFile => {
                    handleManualSetValue('customLogo', dataFile.path)
                  }}
                />
              </div>
              {eventType === EventTypeEnum.Group && (
                <FormInputNumber
                  name="maxParticipants"
                  required
                  className={'w-min'}
                  value={
                    watch('maxParticipants') === null ? 0 : (watch('maxParticipants') as number)
                  }
                  onChange={value => handleManualSetValue('maxParticipants', value)}
                  type={'integer'}
                  label={
                    <Hint label={t('dashboard.calendar.max_participants_hint')}>
                      {t('dashboard.calendar.max_participants_label')}
                    </Hint>
                  }
                  error={errors.maxParticipants?.message}
                />
              )}

              <div>
                <FieldLabel label={t('global.description')} />
                <TipTapEditor
                  extensions={advancedTextEditorExtensions}
                  content={watch('description')}
                  onChange={data => {
                    const initDescription = eventData?.description ?? defaultValues.description

                    setValue('description', data)

                    if (initDescription !== data) {
                      setIsChanged(true)
                    }
                  }}
                  className={twJoin(
                    'min-h-[126px]',
                    errors.description?.message && 'border-danger',
                  )}
                />
                {errors.description?.message && (
                  <FieldErrorAndDescription className="mt-1" error={errors.description?.message} />
                )}
              </div>

              <HostEmailAddress googleIntegrationId={watch('googleIntegrationId')} />

              <AddToGoogleCalendar
                locations={watch('locations')}
                googleCalendarId={watch('googleCalendarId')}
                googleIntegrationId={watch('googleIntegrationId')}
                googleCalendarError={errors.googleCalendarId?.message}
                googleIntegrationError={errors.googleIntegrationId?.message}
                setGoogleCalendarId={id => handleManualSetValue('googleCalendarId', id)}
                setGoogleIntegrationId={id => handleManualSetValue('googleIntegrationId', id)}
              />
            </div>
          </SettingsSection>
          <SettingsSection headingTitle={t('dashboard.calendar.title_availability')}>
            <div className={'flex w-fit flex-col gap-2'}>
              <FieldLabel label={t('dashboard.calendar.invitees_book')} required />
              <RadioGroupRhf
                control={control}
                name="dateRangeMode"
                options={[
                  {
                    id: EventDateRangeModeEnum.Indefinite,
                    caption: t('dashboard.calendar.indefinitely_into_the_future'),
                  },
                  {
                    id: EventDateRangeModeEnum.FarOutBookRange,
                    additionalInfo: (
                      <div
                        className={'flex items-center gap-2'}
                        onMouseDown={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                      >
                        <TransLoco
                          tICU={tICU}
                          i18nKey={'dashboard.calendar.far_out_book_range'}
                          values={{ count: watch('farOutBookRange') }}
                          components={{
                            input: (
                              <FormInputNumber
                                inputClassName={'field-sizing-content'}
                                value={watch('farOutBookRange') ?? 0}
                                onChange={value =>
                                  handleManualSetValue('farOutBookRange', value, {
                                    shouldDirty: true,
                                  })
                                }
                                max={365}
                                disabled={
                                  watch('dateRangeMode') !== EventDateRangeModeEnum.FarOutBookRange
                                }
                                type={'integer'}
                              />
                            ),
                          }}
                        />
                      </div>
                    ),
                  },
                  {
                    id: EventDateRangeModeEnum.Range,
                    caption: t('dashboard.calendar.within_date_range'),
                  },
                ]}
                setValue={handleManualSetValue}
                className="[&>ul]:flex-col [&>ul]:items-start [&>ul]:gap-3 "
              />
              {watch('dateRangeMode') === EventDateRangeModeEnum.Range && (
                <DateRangePicker
                  date={[watch('startDate'), watch('endDate')]}
                  onChange={([startDate, endDate]) => {
                    if (startDate && endDate) {
                      handleManualSetValue('startDate', startDate)
                      handleManualSetValue('endDate', endDate)
                      clearErrors('startDate')
                    }
                  }}
                  timezone={user?.timezone}
                  createdAt={user?.createdAt}
                  intlLocale={intlLocale}
                  customProps={{
                    minDate: getToday(user?.timezone),
                    filterTime: time => filterPassedTime(time, user?.timezone),
                  }}
                  showActionsSideBar={false}
                />
              )}
              <FieldErrorAndDescription
                className="w-full"
                error={[
                  errors.startDate?.message,
                  errors.endDate?.message,
                  errors.dateRangeMode?.message,
                ]
                  .filter(Boolean)
                  .join('<br/>')}
              />
            </div>
            <div>
              <FieldLabel label={t('dashboard.calendar.event_minimum_notice_period_lable')} />
              <div className={'flex items-center gap-2'}>
                <TransLoco
                  t={t}
                  i18nKey={'dashboard.calendar.event_minimum_notice_period'}
                  components={{
                    input: (
                      <TimeDelayPicker
                        classNameInputInteger={'w-[65px]'}
                        selectedTimeType={selectedTime.nearestBookRange.timeType}
                        selectedTimeValue={selectedTime.nearestBookRange.timeValue}
                        onChange={(value, timeType) => {
                          const seconds = convertTimeValueToSeconds(value, timeType)
                          setSelectedTime(props => ({
                            ...props,
                            nearestBookRange: {
                              ...props.nearestBookRange,
                              timeType: timeType,
                              timeValue: value,
                            },
                          }))
                          handleManualSetValue('nearestBookRange', seconds, { shouldDirty: true })
                        }}
                        max={365}
                      />
                    ),
                  }}
                />
              </div>
              {errors?.nearestBookRange?.message && (
                <FieldErrorAndDescription
                  className={'mt-1'}
                  error={errors?.nearestBookRange?.message}
                />
              )}
            </div>
            <div>
              <FieldLabel label={t('dashboard.calendar.cancellation_deadline_label')} />
              <div className={'flex items-center gap-2'}>
                <TransLoco
                  t={t}
                  i18nKey={'dashboard.calendar.cancellation_deadline'}
                  components={{
                    input: (
                      <TimeDelayPicker
                        classNameInputInteger={'w-[65px]'}
                        selectedTimeType={selectedTime.cancellationDeadline.timeType}
                        selectedTimeValue={selectedTime.cancellationDeadline.timeValue}
                        onChange={(value, timeType) => {
                          const seconds = convertTimeValueToSeconds(value, timeType)
                          setSelectedTime(props => ({
                            ...props,
                            cancellationDeadline: {
                              ...props.cancellationDeadline,
                              timeType: timeType,
                              timeValue: value,
                            },
                          }))
                          handleManualSetValue(
                            'cancellationDeadline',
                            seconds === 0 ? null : seconds,
                            {
                              shouldDirty: true,
                            },
                          )
                        }}
                        max={365}
                      />
                    ),
                  }}
                />
              </div>
              {errors?.cancellationDeadline?.message && (
                <FieldErrorAndDescription
                  className={'mt-1'}
                  error={errors?.cancellationDeadline?.message}
                />
              )}
            </div>
            <EventAvailability
              useGlobalAvailability={watch('useGlobalAvailability')}
              defaultSlots={getValues('localUserAvailability.dailySettings')}
              globalSlots={
                eventData
                  ? eventData.globalUserAvailability?.dailySettings
                  : globalAvailability?.dailySettings
              }
              checkboxError={
                !isGlobalAvailabilityCreated &&
                t('dashboard.calendar.event_empty_availability_error')
              }
              error={errors.localUserAvailability?.dailySettings?.message}
              onSelectGlobalAvailability={value =>
                handleManualSetValue('useGlobalAvailability', value)
              }
              onChange={updatedSlots =>
                setValue('localUserAvailability.dailySettings', updatedSlots)
              }
              minDate={minAvailibleDate}
              maxDate={maxAvailibleDate}
            />
            <div className="grid max-w-full grid-cols-1 gap-5 sm:max-w-lg sm:grid-cols-2 ">
              <div className="[&>div]:w-full flex flex-col justify-between gap-4 ">
                <div>
                  <FormSelectRhf
                    control={control}
                    name="startTimeIncrement"
                    data={[
                      ...staticMinutes.map(min => ({
                        id: min,
                        caption: tICU('global.name_of_times.minutes_with_count_icu', {
                          count: min,
                        }),
                      })),
                      {
                        id: CUSTOM_FIELD_VALUE_ID,
                        caption: t('global.custom'),
                      },
                    ]}
                    label={
                      <Hint label={t('dashboard.calendar.event_start_time_increments_tooltip')}>
                        <FieldLabel
                          label={t('dashboard.calendar.event_start_time_increments')}
                          required
                        />
                      </Hint>
                    }
                    setValue={handleManualSetValue}
                    rules={{ required: t('dashboard.validation.empty_error.title') }}
                    error={errors.startTimeIncrement?.message}
                    withoutCloseIcon
                  />
                </div>
                {watch('startTimeIncrement') === CUSTOM_FIELD_VALUE_ID && (
                  <FormInputRhf
                    control={control}
                    name="customStartTimeIncrement"
                    setValue={handleManualSetValue}
                    rules={{ required: t('dashboard.validation.empty_error.title') }}
                    error={errors.customStartTimeIncrement?.message}
                    label={t('global.name_of_times.minutes.plural')}
                    required
                    type="number"
                  />
                )}
              </div>
              <FormInputRhf
                control={control}
                name="maxBookingsPerDay"
                setValue={handleManualSetValue}
                label={
                  <Hint label={t('dashboard.calendar.event_max_bookings_per_day_tooltip')}>
                    <FieldLabel label={t('dashboard.calendar.event_max_bookings_per_day')} />
                  </Hint>
                }
                type="number"
                error={errors.maxBookingsPerDay?.message}
                placeholder={t('dashboard.calendar.event_max_bookings_per_day')}
              />
              <div className="flex items-center gap-2 sm:col-span-2">
                <Hint label={t('dashboard.calendar.buffer_time_tooltip')}>
                  <FieldLabel
                    labelClassName={'text-text-primary text-base'}
                    label={t('dashboard.calendar.buffer_time')}
                  />
                </Hint>
              </div>
              <div className={'flex flex-col gap-5'}>
                <div className="[&>div]:w-full">
                  <FormSelectRhf
                    control={control}
                    name="bookingBufferTime"
                    data={[null, ...staticMinutes].map(min => {
                      const value = min === null ? 0 : min

                      return {
                        id: min,
                        caption: tICU('global.name_of_times.minutes_with_count_icu', {
                          count: value,
                        }),
                      }
                    })}
                    label={
                      <FieldLabel label={t('dashboard.calendar.event_buffer_time_before_after')} />
                    }
                    setValue={handleManualSetValue}
                    error={errors.bookingBufferTime?.message}
                    withoutCloseIcon
                  />
                </div>
                <div className="[&>div]:w-full">
                  <FieldLabel
                    label={t('dashboard.calendar.time_zone_display')}
                    labelClassName="mb-2"
                  />
                  <RadioGroupRhf
                    control={control}
                    name="displayTimezoneType"
                    options={[
                      {
                        id: EventTimeZoneTypeEnum.Local,
                        caption: t('dashboard.calendar.use_local_timezone'),
                      },
                      {
                        id: EventTimeZoneTypeEnum.Fixed,
                        caption: t('dashboard.calendar.use_fixed_timezone'),
                      },
                    ]}
                    setValue={handleManualSetValue}
                    className="[&>ul]:flex-col [&>ul]:items-start"
                  />
                </div>
              </div>
            </div>
          </SettingsSection>
        </div>
        {!!Object.keys(errors).length && (
          <FieldErrorAndDescription
            className="text-end -mt-6 w-full"
            error={t('dashboard.calendar.event_validation.title')}
          />
        )}
      </form>
      <ConfirmationModalBeforeExit
        confirmationText={t('dashboard.calendar.confirmation_message_before_exit')}
        hideModal={!isChanged}
      />
    </>
  )
}
